import { useMemo, useState } from "react";
import {
  Loading,
  Button,
  Badge,
  Modal,
  SummaryCard,
  SegmentedControl,
  ClinicFilterBar,
  getCurrentMonth,
  usePersistedState,
  showSuccess,
  showApiError,
} from "@common";
import { formatMoney, NETSUITE_ACCOUNT_IDS, canManageTransfers } from "@utils";
import { useAuth } from "@api/useAuth";
import useClinicFilterOptions from "@hooks/useClinicFilterOptions";
import {
  useTransfers,
  useCreateTransfer,
  useUpdateTransfer,
  useBankAccounts,
  useSearchNetsuiteAdvancedIntercompanyJournalEntries,
  useCreateNetsuiteIntercompanyJournalEntry,
} from "@api/hooks";
import TransferModal from "./TransferModal";
import IntercompanyTransferPreview from "./IntercompanyTransferPreview";

const SFC_BANK_ID = NETSUITE_ACCOUNT_IDS.RBC_SF_CORPORATION_9411;

// Build the account-option shape the intercompany preview expects, from a
// transfer's stored account fields.
function accountOptOf(transfer, side) {
  if (!transfer) return null;
  return {
    bank_account_id: transfer[`netsuite_${side}_account_id`],
    bank_name: transfer[`${side}_account_name`],
    clinic: transfer[`${side}_clinic_code`],
    clinic_name: transfer[`${side}_clinic_name`],
  };
}

// Entity (subsidiary) name for a transfer side — clinic name or "SFC". Must
// mirror the intercompany preview's memo wording.
function sideEntityName(transfer, side) {
  if (Number(transfer[`netsuite_${side}_account_id`]) === SFC_BANK_ID) return "SFC";
  return transfer[`${side}_clinic_name`] || transfer[`${side}_clinic_code`] || "";
}

function intercompanyMemoOf(transfer) {
  return `Intercompany ${sideEntityName(transfer, "from")} to ${sideEntityName(
    transfer,
    "to",
  )} - Smiles Analytics`;
}

// Subsidiary id for a transfer side: the clinic's NetSuite subsidiary, or the
// SFC subsidiary for the corporate account.
function sideSubsidiaryId(transfer, side) {
  if (Number(transfer[`netsuite_${side}_account_id`]) === SFC_BANK_ID) {
    return NETSUITE_ACCOUNT_IDS.SMILES_FIRST_CORPORATION;
  }
  return transfer[`${side}_clinic_subsidiary_id`] ?? null;
}

// Build the advanced intercompany JE payload for a transfer: 4 balanced lines
// (bank + intercompany-loan leg per side). Returns null if a subsidiary is
// missing (can't post a valid JE).
function buildTransferJePayload(transfer) {
  const fromSub = sideSubsidiaryId(transfer, "from");
  const toSub = sideSubsidiaryId(transfer, "to");
  if (fromSub == null || toSub == null) return null;
  const amt = Number(transfer.amount) || 0;
  const memo = intercompanyMemoOf(transfer);
  const loan = String(NETSUITE_ACCOUNT_IDS.LOAN_FROM_SMILES_FIRST_CORPORATION);
  return {
    subsidiary: { id: String(fromSub) },
    memo,
    trandate: transfer.date,
    line: {
      items: [
        {
          lineSubsidiary: { id: String(fromSub) },
          account: { id: String(transfer.netsuite_from_account_id) },
          credit: amt,
          memo,
        },
        { lineSubsidiary: { id: String(fromSub) }, account: { id: loan }, debit: amt, memo },
        {
          lineSubsidiary: { id: String(toSub) },
          account: { id: String(transfer.netsuite_to_account_id) },
          debit: amt,
          memo,
        },
        { lineSubsidiary: { id: String(toSub) }, account: { id: loan }, credit: amt, memo },
      ],
    },
  };
}

// Normalise a NetSuite trandate (ISO or M/D/YYYY) to YYYY-MM-DD.
function ymd(s) {
  if (!s) return "";
  const str = String(s);
  const iso = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const us = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (us) return `${us[3]}-${us[1].padStart(2, "0")}-${us[2].padStart(2, "0")}`;
  return str.slice(0, 10);
}

// Looks up the posted intercompany JE for a transfer (by memo, matched on
// trandate) and renders a link to it in NetSuite.
function TransferJeLink({ transfer }) {
  const memo = intercompanyMemoOf(transfer);
  const { data, isLoading } = useSearchNetsuiteAdvancedIntercompanyJournalEntries({ memo });
  const items = data?.items ?? data?.results ?? (Array.isArray(data) ? data : []);
  const match = items.find((r) => ymd(r.trandate) === String(transfer.date)) || null;

  if (isLoading) return <span className="text-palette-gray-placeholder">…</span>;
  if (!match) return <span className="text-palette-gray-placeholder">—</span>;
  const url = `https://4571901.app.netsuite.com/app/accounting/transactions/icjournal.nl?id=${match.id}`;
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="font-medium text-indigo-600 hover:underline"
    >
      {match.tranid || match.transactionnumber || "View JE"}
    </a>
  );
}

// ─── helpers ──────────────────────────────────────────────────────────────────

const STATUS_META = {
  pending: { label: "Pending", color: "warning" },
  approved: { label: "Approved", color: "success" },
  rejected: { label: "Rejected", color: "danger" },
};

function fmtDateShort(dateStr) {
  if (!dateStr) return "—";
  return new Date(`${dateStr}T00:00:00`).toLocaleDateString("en-CA", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// YYYY-MM → { date_from, date_to } spanning that whole month.
function monthRange(month) {
  if (!month) return {};
  const [y, m] = month.split("-").map(Number);
  const last = new Date(y, m, 0).getDate();
  return { date_from: `${month}-01`, date_to: `${month}-${String(last).padStart(2, "0")}` };
}

const VIEW_OPTIONS = (pendingCount) => [
  { value: "transfers", label: "Transfers" },
  { value: "approvals", label: `Approvals · ${pendingCount}` },
];

// Columns depend on the view: Transfers shows the posted-JE Link; Approvals
// shows the Approve/Reject actions.
function tableColsFor(view) {
  const base = [
    { key: "from", label: "From Account", align: "left" },
    { key: "to", label: "To Account", align: "left" },
    { key: "amount", label: "Amount", align: "right" },
    { key: "date", label: "Date", align: "right" },
    { key: "notes", label: "Notes", align: "left" },
    { key: "status", label: "Status", align: "left" },
  ];
  return view === "approvals"
    ? [...base, { key: "action", label: "", align: "right" }]
    : [...base, { key: "link", label: "Link", align: "left" }];
}

// ─── row ──────────────────────────────────────────────────────────────────────

// NetSuite names come through as "Bank Balance : Bank Balance : RBC … - [1234]".
function cleanBankName(name) {
  if (!name) return "";
  return name.replace(/^(?:\s*Bank Balance\s*:\s*)+/i, "").trim();
}

function Party({ accountName, clinicName, clinicCode }) {
  const sub = [clinicName, clinicCode].filter(Boolean).join(" · ");
  return (
    <div className="min-w-0">
      <div
        className="font-semibold text-palette-gray-primary truncate"
        title={accountName || undefined}
      >
        {cleanBankName(accountName) || clinicName || clinicCode || "—"}
      </div>
      {sub && <div className="text-xs text-palette-gray-secondary truncate">{sub}</div>}
    </div>
  );
}

function TransferRow({ transfer, view, onApprove, onReject, saving }) {
  const status = STATUS_META[transfer.status] ?? STATUS_META.pending;
  const isPending = transfer.status === "pending";
  const isApprovals = view === "approvals";
  return (
    <tr className="border-b border-palette-gray-divider last:border-0">
      <td className="px-4 py-3 max-w-[220px]">
        <Party
          accountName={transfer.from_account_name}
          clinicName={transfer.from_clinic_name}
          clinicCode={transfer.from_clinic_code}
        />
      </td>
      <td className="px-4 py-3 max-w-[220px]">
        <Party
          accountName={transfer.to_account_name}
          clinicName={transfer.to_clinic_name}
          clinicCode={transfer.to_clinic_code}
        />
      </td>
      <td className="px-4 py-3 text-right whitespace-nowrap font-semibold tabular-nums text-palette-gray-primary">
        {formatMoney(transfer.amount)}
      </td>
      <td className="px-4 py-3 text-right whitespace-nowrap text-palette-gray-heading">
        {fmtDateShort(transfer.date)}
      </td>
      <td className="px-4 py-3 max-w-[240px]">
        <span className="text-sm text-palette-gray-secondary line-clamp-2">
          {transfer.notes || "—"}
        </span>
      </td>
      <td className="px-4 py-3">
        <Badge color={status.color}>{status.label}</Badge>
      </td>
      {isApprovals ? (
        <td className="px-4 py-3 text-right whitespace-nowrap">
          {isPending && onApprove ? (
            <div className="flex items-center justify-end gap-2">
              <Button
                variant="success"
                size="sm"
                disabled={saving}
                onClick={() => onApprove(transfer)}
              >
                Approve &amp; post intercompany
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={saving}
                onClick={() => onReject(transfer)}
              >
                Reject
              </Button>
            </div>
          ) : (
            <span className="text-palette-gray-placeholder">—</span>
          )}
        </td>
      ) : (
        <td className="px-4 py-3 whitespace-nowrap text-sm">
          <TransferJeLink transfer={transfer} />
        </td>
      )}
    </tr>
  );
}

// ─── main ─────────────────────────────────────────────────────────────────────

export default function Actions({ canEdit: canEditProp }) {
  const { user } = useAuth();
  // Write access mirrors the backend `IsAdminOrganizerOrPayroll` on
  // apps/transfer (can_act_on_payroll) — Bookkeeper reaches this tab and can
  // read transfers, but cannot create/approve/reject them.
  const canEdit = canEditProp ?? canManageTransfers(user);

  const [view, setView] = usePersistedState("cashFlow:actionsView", "transfers");
  const [months, setMonths] = usePersistedState("cashFlow:actionsMonths", () => [
    getCurrentMonth(),
  ]);
  const [areaManagerFilter, setAreaManagerFilter] = usePersistedState(
    "cashFlow:actionsAreaManager",
    "",
  );
  const [clinicFilters, setClinicFilters] = usePersistedState("cashFlow:actionsClinics", []);
  const [modalOpen, setModalOpen] = useState(false);
  const [approving, setApproving] = useState(null); // transfer pending approval

  const { clinicsQuery, areaManagerOptions, clinicOptions } =
    useClinicFilterOptions(areaManagerFilter);

  // Account options for the New Transfer modal, from the local registry — the
  // display name is the stored `label`, so no NetSuite call is needed.
  // Mapped to the field names TransferModal expects (`bank_account_id`, etc.).
  //
  // Accounts with no netsuite id are filtered out: a transfer posts an
  // intercompany JE keyed on NetSuite accounts, so a manually-added account
  // (Dens N Dente's RBC pair) can't be either side of one.
  const bankAccountsQuery = useBankAccounts({ status: "active", limit: 500 });
  const bankAccounts = useMemo(() => {
    const rows = (bankAccountsQuery.data?.results ?? [])
      .filter((a) => a.netsuite_account_id != null)
      .map((a) => ({
        // Local BankAccount PK — what a transfer is WRITTEN with.
        id: a.id,
        // NetSuite account id — what the JE is POSTED with, and what the
        // <select> is keyed on. Never send this as from_bank_account.
        bank_account_id: a.netsuite_account_id,
        bank_name: a.label,
        clinic: a.clinic_code,
        clinic_name: a.clinic_name || a.entity_name,
      }));
    // SFC always at the top of the account picker
    return rows.sort((a, b) => (a.clinic === "SFC" ? -1 : b.clinic === "SFC" ? 1 : 0));
  }, [bankAccountsQuery.data]);

  // Filter by clinic_id rather than code — matches either side of the transfer
  // (from_clinic / to_clinic) on the FK columns directly.
  const selectedClinicIds = useMemo(() => {
    if (clinicFilters.length === 0) return [];
    const idByCode = new Map(clinicOptions.map((o) => [o.value, o.id]));
    return clinicFilters.map((code) => idByCode.get(code)).filter((id) => id != null);
  }, [clinicFilters, clinicOptions]);

  const listFilters = useMemo(
    () => ({
      ...monthRange(months[0]),
      ...(selectedClinicIds.length ? { clinic_id: selectedClinicIds.join(",") } : {}),
    }),
    [months, selectedClinicIds],
  );
  // Without a clinic_id the endpoint returns every clinic's transfers, so hold
  // the request until something is picked.
  const transfersQuery = useTransfers(listFilters, {
    enabled: selectedClinicIds.length > 0,
  });
  const createTransfer = useCreateTransfer();
  const updateTransfer = useUpdateTransfer();
  const createJe = useCreateNetsuiteIntercompanyJournalEntry();

  // Post the advanced intercompany JE for a (created/approved) transfer.
  async function postIntercompanyJe(transfer) {
    const jePayload = buildTransferJePayload(transfer);
    if (!jePayload) {
      showApiError(
        new Error("Missing NetSuite subsidiary for one side — can't post the intercompany JE."),
      );
      return false;
    }
    await createJe.mutateAsync(jePayload);
    return true;
  }

  const transfers = useMemo(
    () => transfersQuery.data?.results ?? transfersQuery.data ?? [],
    [transfersQuery.data],
  );

  const totals = useMemo(() => {
    const acc = {
      pending: { count: 0, amount: 0 },
      approved: { count: 0, amount: 0 },
      rejected: { count: 0, amount: 0 },
      all: { count: transfers.length, amount: 0 },
    };
    transfers.forEach((t) => {
      const amt = Number(t.amount) || 0;
      acc.all.amount += amt;
      const bucket = acc[t.status];
      if (bucket) {
        bucket.count += 1;
        bucket.amount += amt;
      }
    });
    return acc;
  }, [transfers]);

  const visibleTransfers = useMemo(
    () => (view === "approvals" ? transfers.filter((t) => t.status === "pending") : transfers),
    [transfers, view],
  );

  const saving = createTransfer.isPending || updateTransfer.isPending || createJe.isPending;
  const isLoading = transfersQuery.isLoading || clinicsQuery.isLoading;

  async function handleSetStatus(transfer, status) {
    try {
      await updateTransfer.mutateAsync({ id: transfer.id, status });
      showSuccess(`Transfer ${status}.`);
    } catch (err) {
      showApiError(err);
    }
  }

  // Approving posts the advanced intercompany JE first, then marks approved.
  async function confirmApprove() {
    try {
      const posted = await postIntercompanyJe(approving);
      if (!posted) return;
      await updateTransfer.mutateAsync({ id: approving.id, status: "approved" });
      showSuccess("Approved & intercompany JE posted.");
      setApproving(null);
    } catch (err) {
      showApiError(err);
    }
  }

  async function handleCreate(payload) {
    try {
      // New transfers are staged as pending — the intercompany JE is posted and
      // the status set to approved later, in the Approvals tab.
      await createTransfer.mutateAsync({ ...payload, status: "pending" });
      showSuccess("Transfer created (pending approval).");
      setModalOpen(false);
    } catch (err) {
      showApiError(err);
    }
  }

  return (
    <>
      {/* Summary cards */}
      <div className="flex flex-wrap gap-3 mb-4">
        <SummaryCard
          label="Pending"
          value={totals.pending.count}
          tag={`${formatMoney(totals.pending.amount)} to move`}
          valueClass="text-palette-amber-badge"
        />
        <SummaryCard
          label="Approved"
          value={totals.approved.count}
          tag={formatMoney(totals.approved.amount)}
          valueClass="text-smiles-green"
        />
        <SummaryCard label="Rejected" value={totals.rejected.count} />
        <SummaryCard
          label="Total Transfers"
          value={totals.all.count}
          tag={formatMoney(totals.all.amount)}
        />
      </div>

      {/* Filters */}
      <ClinicFilterBar
        months={months}
        onMonthsChange={setMonths}
        areaManagerFilter={areaManagerFilter}
        areaManagerOptions={areaManagerOptions}
        onAreaManagerChange={(v) => {
          setAreaManagerFilter(v);
          setClinicFilters([]);
        }}
        clinicOptions={clinicOptions}
        clinicValue={clinicFilters}
        onClinicChange={setClinicFilters}
      />

      <div className="rounded-2xl border border-palette-gray-divider bg-white shadow-sm overflow-hidden">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-palette-gray-divider px-5 py-4">
          <SegmentedControl
            options={VIEW_OPTIONS(totals.pending.count)}
            value={view}
            onChange={setView}
          />
          {canEdit && (
            <Button variant="primary" size="sm" onClick={() => setModalOpen(true)}>
              + New Transfer
            </Button>
          )}
        </div>

        {/* Body */}
        {isLoading ? (
          <Loading text="Loading transfers..." />
        ) : visibleTransfers.length === 0 ? (
          <div className="flex items-center justify-center py-16 text-sm text-palette-gray-placeholder">
            {selectedClinicIds.length === 0
              ? // Nothing requested yet — an empty table here would read as
                // "this clinic has no transfers", which isn't what happened.
                "Select one or more clinics to view transfers."
              : view === "approvals"
                ? "No transfers awaiting approval."
                : "No transfers yet."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="cf-table w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-palette-gray-divider bg-palette-gray-subtle">
                  {tableColsFor(view).map((col) => (
                    <th
                      key={col.key}
                      className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider text-palette-gray-secondary whitespace-nowrap ${
                        col.align === "right" ? "text-right" : "text-left"
                      }`}
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleTransfers.map((transfer) => (
                  <TransferRow
                    key={transfer.id}
                    transfer={transfer}
                    view={view}
                    onApprove={canEdit ? (t) => setApproving(t) : undefined}
                    onReject={canEdit ? (t) => handleSetStatus(t, "rejected") : undefined}
                    saving={saving}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <TransferModal
        isOpen={canEdit && modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleCreate}
        clinicOptions={bankAccounts}
        saving={saving}
      />

      <Modal
        isOpen={canEdit && !!approving}
        onClose={() => setApproving(null)}
        title="Approve transfer"
        subtitle="Review and post the intercompany transfer"
        size="xl"
        footer={
          <>
            <Button variant="ghost" onClick={() => setApproving(null)} disabled={saving}>
              Cancel
            </Button>
            <Button variant="primary" onClick={confirmApprove} disabled={saving}>
              {createJe.isPending
                ? "Posting intercompany JE…"
                : updateTransfer.isPending
                  ? "Approving…"
                  : "Approve & post"}
            </Button>
          </>
        }
      >
        <IntercompanyTransferPreview
          fromOpt={accountOptOf(approving, "from")}
          toOpt={accountOptOf(approving, "to")}
          amount={approving?.amount}
          transferDate={approving?.date}
        />
      </Modal>
    </>
  );
}
